import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/shared/ui/button';
import { AppDetailLayout } from '@/shared/app-detail-layout';
import { ProviderListSection } from './parts/provider-list';
import { DeploymentSettingsSection } from './parts/deployment-settings';

/**
 * The platform AI console.
 *
 * Two surfaces, one page: the providers this deployment can call, and the
 * deployment-wide defaults those providers are reached through. Tenant
 * availability lives on its own route because it is a list of tenants rather
 * than a setting of the deployment.
 */
export function AiConsolePage(): ReactNode {
  const navigate = useNavigate();

  const tabs = [
    { id: 'providers', label: 'Providers' },
    { id: 'deployment', label: 'Deployment' },
  ];

  const sections = [
    { id: 'providers', label: 'Providers', content: <ProviderListSection /> },
    { id: 'deployment', label: 'Deployment', content: <DeploymentSettingsSection /> },
  ];

  return (
    <AppDetailLayout
      headerVariant="accent"
      title="AI console"
      subtitle="Providers, deployment defaults and tenant availability for this deployment."
      breadcrumbs={[
        { label: 'Platform', href: '/platform/tenants' },
        { label: 'AI' },
      ]}
      tabs={tabs}
      sections={sections}
      tabsInHeader
      primaryAction={
        <Button
          onClick={() => {
            void navigate({ to: '/platform/ai/providers/new' });
          }}
        >
          Add provider
        </Button>
      }
      secondaryAction={
        <Button
          variant="secondary"
          onClick={() => {
            void navigate({ to: '/platform/ai/tenants' });
          }}
        >
          Tenant availability
        </Button>
      }
    />
  );
}
